import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import PerformanceMonitor from './components/PerformanceMonitor';
import OfflineSupport from './components/OfflineSupport';

import { MobileOptimizer } from './utils/mobileOptimizer';
import performanceManager from './utils/performanceManager';

// Import page components
import HomePage from './pages/HomePage';
import GenesPage from './pages/GenesPage';
import TravelPage from './pages/TravelPage';
import PlymouthPage from './pages/PlymouthPage';
import RawrockPage from './pages/RawrockPage';
import TomatoJarsPage from './pages/TomatoJarsPage';
import April122025Page from './pages/April122025Page';
import FutbolPage from './pages/FutbolPage';
import WeddingPage from './pages/WeddingPage';
import SouthingtonPage from './pages/SouthingtonPage';
import VinoPage from './pages/VinoPage';
import JeepPage from './pages/JeepPage';
import ThanksgivingPage from './pages/ThanksgivingPage';
import CalendarPage from './pages/CalendarPage';
import PanePage from './pages/PanePage';
import RossettiPage from './pages/RossettiPage';

import PersonDetailsPage from './pages/PersonDetailsPage';
import FamilyTreePage from './pages/FamilyTreePage';
import FamilyStatsPage from './pages/FamilyStatsPage';

function App() {
  // Initialize mobile optimizations and performance monitoring on app startup
  useEffect(() => {
    // Initialize mobile optimizations
    MobileOptimizer.initialize();
    
    // Track app initialization
    const appLoadingState = performanceManager.trackLoadingStart('app-initialization');
    
    // Simulate app initialization completion
    setTimeout(() => {
      performanceManager.trackLoadingEnd('app-initialization', true);
    }, 100);

    // Set up global error handling
    const handleUnhandledError = (event) => {
      performanceManager.trackError('global-error', event.error || event.reason, {
        type: 'unhandled-error',
        source: event.filename || 'unknown',
        line: event.lineno || 0,
        column: event.colno || 0
      });
    };

    const handleUnhandledRejection = (event) => {
      performanceManager.trackError('global-promise-rejection', event.reason, {
        type: 'unhandled-promise-rejection'
      });
    };

    // Add global error listeners
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary componentName="App">
        <OfflineSupport>
          <Router>
              {/* Performance monitoring in development */}
              {process.env.NODE_ENV === 'development' && <PerformanceMonitor />}
              
              <Layout>
                <Routes>
                  <Route path="/" element={
                    <ErrorBoundary componentName="HomePage">
                      <HomePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/southington" element={
                    <ErrorBoundary componentName="SouthingtonPage">
                      <SouthingtonPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/person/:personId" element={
                    <ErrorBoundary componentName="PersonDetailsPage">
                      <PersonDetailsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/futbol" element={
                    <ErrorBoundary componentName="FutbolPage">
                      <FutbolPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/travel" element={
                    <ErrorBoundary componentName="TravelPage">
                      <TravelPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/family-tree" element={
                    <ErrorBoundary componentName="FamilyTreePage">
                      <FamilyTreePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/calendar" element={
                    <ErrorBoundary componentName="CalendarPage">
                      <CalendarPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/pane" element={
                    <ErrorBoundary componentName="PanePage">
                      <PanePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/rossetti" element={
                    <ErrorBoundary componentName="RossettiPage">
                      <RossettiPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/weddings" element={
                    <ErrorBoundary componentName="WeddingPage">
                      <WeddingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/thanksgiving" element={
                    <ErrorBoundary componentName="ThanksgivingPage">
                      <ThanksgivingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/family-stats" element={
                    <ErrorBoundary componentName="FamilyStatsPage">
                      <FamilyStatsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/plymouth" element={
                    <ErrorBoundary componentName="PlymouthPage">
                      <PlymouthPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/rawrock" element={
                    <ErrorBoundary componentName="RawrockPage">
                      <RawrockPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/tomatojars" element={
                    <ErrorBoundary componentName="TomatoJarsPage">
                      <TomatoJarsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/april-12-2025" element={
                    <ErrorBoundary componentName="April122025Page">
                      <April122025Page />
                    </ErrorBoundary>
                  } />
                  <Route path="/jeep" element={
                    <ErrorBoundary componentName="JeepPage">
                      <JeepPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/vino" element={
                    <ErrorBoundary componentName="VinoPage">
                      <VinoPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/genes" element={
                    <ErrorBoundary componentName="GenesPage">
                      <GenesPage />
                    </ErrorBoundary>
                  } />
                  {/* Fallback route for 404 */}
                  <Route path="*" element={
                    <ErrorBoundary componentName="HomePage">
                      <HomePage />
                    </ErrorBoundary>
                  } />
                </Routes>
              </Layout>
            </Router>
        </OfflineSupport>
    </ErrorBoundary>
  );
}

export default App;
